import type { MarkerFeature } from "../../../../../utils/models/MarkerFeature";
import type { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import type { UndoCommand, CommandContext } from "../types";

/**
 * Command for splitting a route into two at a marker index.
 * Stores explicit snapshots of the original route (before), the truncated
 * original (after), and the new route for reliable undo/redo.
 */
export class SplitRouteCommand implements UndoCommand {
  readonly type = 'SPLIT_ROUTE';
  readonly routeKey: string;

  /** Original route data before split */
  private originalMarkers: MarkerFeature[];
  private originalLineStrings: LineStringFeature[];
  /** Truncated original route data after split */
  private truncatedMarkers: MarkerFeature[];
  private truncatedLineStrings: LineStringFeature[];
  /** New route created by the split */
  private newRouteKey: string;
  private newRouteSnapshot: any;
  /** Position state */
  private routePositionsBefore: { key: string; position: number }[];
  private routePositionsAfter: { key: string; position: number }[];
  private activeRouteKeyAfter: string;

  constructor(data: {
    routeKey: string;
    originalMarkers: MarkerFeature[];
    originalLineStrings: LineStringFeature[];
    truncatedMarkers: MarkerFeature[];
    truncatedLineStrings: LineStringFeature[];
    newRouteKey: string;
    newRouteSnapshot: any;
    routePositionsBefore: { key: string; position: number }[];
    routePositionsAfter: { key: string; position: number }[];
    activeRouteKeyAfter: string;
  }) {
    this.routeKey = data.routeKey;
    this.originalMarkers = JSON.parse(JSON.stringify(data.originalMarkers));
    this.originalLineStrings = JSON.parse(JSON.stringify(data.originalLineStrings));
    this.truncatedMarkers = JSON.parse(JSON.stringify(data.truncatedMarkers));
    this.truncatedLineStrings = JSON.parse(JSON.stringify(data.truncatedLineStrings));
    this.newRouteKey = data.newRouteKey;
    this.newRouteSnapshot = JSON.parse(JSON.stringify(data.newRouteSnapshot));
    this.routePositionsBefore = data.routePositionsBefore;
    this.routePositionsAfter = data.routePositionsAfter;
    this.activeRouteKeyAfter = data.activeRouteKeyAfter;
  }

  undo(ctx: CommandContext): void {
    const route = ctx.state.routes[this.routeKey];
    if (!route) return;

    // Restore original route
    route.markers = JSON.parse(JSON.stringify(this.originalMarkers));
    route.lineStrings = JSON.parse(JSON.stringify(this.originalLineStrings));
    route.updated_at = new Date();

    // Remove the new route
    delete ctx.state.routes[this.newRouteKey];

    // Restore positions
    for (const { key, position } of this.routePositionsBefore) {
      if (ctx.state.routes[key]) {
        ctx.state.routes[key].position = position;
      }
    }

    ctx.state.activeRouteKey = this.routeKey;
  }

  redo(ctx: CommandContext): void {
    const route = ctx.state.routes[this.routeKey];
    if (!route) return;

    // Truncate original route to post-split state (explicit, not derived)
    route.markers = JSON.parse(JSON.stringify(this.truncatedMarkers));
    route.lineStrings = JSON.parse(JSON.stringify(this.truncatedLineStrings));
    route.updated_at = new Date();

    // Re-add the new route
    ctx.state.routes[this.newRouteKey] = JSON.parse(JSON.stringify(this.newRouteSnapshot));

    // Restore positions
    for (const { key, position } of this.routePositionsAfter) {
      if (ctx.state.routes[key]) {
        ctx.state.routes[key].position = position;
      }
    }

    ctx.state.activeRouteKey = this.activeRouteKeyAfter;
  }
}
